import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalContent,
  Alert,
  Spinner,
  Tooltip,
} from "@heroui/react";
import { Button } from "../shared";
import { useSocket } from "../../redux/socket/useSocket.js";
import axiosInstance from "../../redux/network/https";
import ProgressiveContractLoader from "./ProgressiveContractLoader";

import { endPoints } from "../../redux/network/constant";

// Utility function to debounce function calls
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

// Helper to map error codes to user-friendly messages
const getErrorMessage = (error, retryCount = 0) => {
  const errorStr = error?.toString?.() || error || "Unknown error";
  
  // Map specific errors to helpful messages
  const errorMap = {
    'INVALID_DESCRIPTION': 'Description is invalid. Check length (10-700 words) and content.',
    'CHANNEL_NOT_FOUND': 'Chat channel not found. Please refresh and try again.',
    'RATE_LIMIT': 'Too many requests. Please wait a moment and try again.',
    'SERVER_ERROR': 'Server error occurred. Our team has been notified. Please try again.',
    'NETWORK_ERROR': 'Network error. Please check your connection and try again.',
    'TIMEOUT': 'Generation took too long. Please try again with a shorter description.',
  };

  // Check if error message contains known patterns
  let friendlyMsg = "Contract generation failed. Please try again.";
  
  for (const [key, msg] of Object.entries(errorMap)) {
    if (errorStr.includes(key)) {
      friendlyMsg = msg;
      break;
    }
  }

  // Fall back to error message if it's not too generic
  if (error && typeof error === 'string' && error.length < 200 && !error.includes('undefined')) {
    friendlyMsg = error;
  }

  // Add retry info if applicable
  if (retryCount > 0) {
    friendlyMsg += ` (Attempt ${retryCount}/3)`;
  }

  return friendlyMsg;
};

const GENERATION_TIMEOUT_MS = 5 * 60 * 1000;

export const ContractModal = ({ isOpen, onClose, chatChannelId }) => {
  const { isSocketReady, sendCommand, subscribeToSocketMessages } = useSocket();

  // Force-unmount guard: ensures the NextUI <Modal> portal is removed even
  // when framer-motion's exit-complete callback never fires.
  const [mounted, setMounted] = useState(isOpen);
  useEffect(() => {
    if (isOpen) { setMounted(true); return; }
    const t = setTimeout(() => setMounted(false), 350);
    return () => clearTimeout(t);
  }, [isOpen]);

  const [state, setState] = useState({
    loading: false,
    error: null,
    contractId: null,
    contractFileUrl: null,
    contractStatus: null,
    selectedContractIndex: 0, // Track the currently selected contract index
    isTransitioning: false,
    generationPhase: "entry", // 'entry' | 'generating' | 'completed'
    descriptionValid: false,
    generationProgress: 0,
    progressMessage: "Preparing to generate...",
    retryCount: 0,
    loadingPdf: false,
    pdfError: null,
  });

  // Ref to store handleGenerateContract to avoid dependency issues in useEffect
  const handleGenerateContractRef = useRef(null);
  
  // Timeout ref for generation safety
  const generationTimeoutRef = useRef(null);

  const [contractDetails, setContractDetails] = useState({
    title: "Contract Agreement",

    description: "",
    chat_channel_id: chatChannelId,
  });

  const [contracts, setContracts] = useState([]);

  // Debounced fetch contracts to prevent race conditions
  const debouncedFetchContracts = useMemo(
    () => debounce(() => {
      if (isSocketReady) {
        sendCommand("fetch_contracts", { channel_id: chatChannelId });
      }
    }, 500),
    [chatChannelId, sendCommand, isSocketReady]
  );

  useEffect(() => {
    if (isSocketReady && isOpen && chatChannelId) {
      sendCommand("fetch_contracts", { channel_id: chatChannelId });
    }
  }, [isSocketReady, isOpen, chatChannelId, sendCommand]);

  // Reset state when modal closes or chat channel changes
  useEffect(() => {
    if (!isOpen) {
      // Reset contract details when modal closes
      setContractDetails({
        title: "Contract Agreement",
        description: "",
        chat_channel_id: chatChannelId,
      });

      // Reset all generation-related state
      setState({
        loading: false,
        error: null,
        contractId: null,
        contractFileUrl: null,
        contractStatus: null,
        selectedContractIndex: 0,
        isTransitioning: false,
        generationPhase: "entry",
        descriptionValid: false,
        generationProgress: 0,
        progressMessage: "Preparing to generate...",
        retryCount: 0,
        loadingPdf: false,
        pdfError: null,
      });

      // Clear contracts
      setContracts([]);
    }
  }, [isOpen, chatChannelId]);

  // Reset state when switching between different chats
  useEffect(() => {
    if (isOpen && chatChannelId) {
      setContractDetails((prev) => ({
        ...prev,
        chat_channel_id: chatChannelId,
        description: "", // Clear description when switching chats
        title: "Contract Agreement", // Reset title
      }));

      setState((prev) => ({
        ...prev,
        selectedContractIndex: 0,
        error: null,
      }));
    }
  }, [chatChannelId, isOpen]);

  // Timeout protection for generation - prevent infinite loading
  useEffect(() => {
    if (state.loading && state.generationPhase === "generating") {
      // Set timeout to prevent infinite loading states
      generationTimeoutRef.current = setTimeout(() => {
        console.warn("Contract generation timeout - 5 minutes exceeded");
        updateState({
          loading: false,
          error: "Generation took too long (5 min timeout). Please try again.",
          generationPhase: "entry",
          retryCount: 0,
        });
      }, GENERATION_TIMEOUT_MS);
    } else {
      // Clear timeout if generation completes or is cancelled
      if (generationTimeoutRef.current) {
        clearTimeout(generationTimeoutRef.current);
        generationTimeoutRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (generationTimeoutRef.current) {
        clearTimeout(generationTimeoutRef.current);
      }
    };
  }, [state.loading, state.generationPhase, updateState]);

  // Update state helper
  const updateState = useCallback((updates) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const setGenerationPhase = useCallback((phase) => {
    setState((prev) => ({ ...prev, generationPhase: phase }));
  }, []);

  // Generate contract with two-step workflow
  const handleGenerateContract = useCallback(async () => {
    // Entry phase: Start generation
    if (state.generationPhase === "entry") {
      if (!state.descriptionValid) {
        updateState({
          error: "Please enter a valid description (10-700 words)",
        });
        return;
      }

      // Reset to latest contract and update form
      if (state.selectedContractIndex !== 0 && contracts.length > 0) {
        const latestContract = contracts[0];
        setContractDetails((prev) => ({
          ...prev,
          title: latestContract.title || "Contract Agreement",
          description: latestContract.description || "",
        }));
      }

      setGenerationPhase("generating");
      updateState({
        loading: true,
        error: null,
        contractId: null,
        contractFileUrl: null,
        contractStatus: null,
        selectedContractIndex: 0,
        retryCount: 0,
        generationProgress: 0,
      });

      try {
        const payload = {
          chat_channel: chatChannelId,
          description: contractDetails.description,
        };

        await axiosInstance.post(endPoints.ContractGenerate, payload);
      } catch (err) {
         console.error("Contract generation error:", err);
         const friendlyError = getErrorMessage(
           err.response?.data?.error || err.message,
           state.retryCount
         );
         updateState({
           loading: false,
           error: friendlyError,
           generationPhase: "entry",
         });
         setGenerationPhase("entry");
       }
    } else {
      // Already generating or completed, prevent double-click
      return;
    }
  }, [
    state.generationPhase,
    state.descriptionValid,
    state.selectedContractIndex,
    state.retryCount,
    contracts,
    contractDetails.description,
    chatChannelId,
    setGenerationPhase,
    updateState,
  ]);

  // Update ref whenever handleGenerateContract changes
  useEffect(() => {
    handleGenerateContractRef.current = handleGenerateContract;
  }, [handleGenerateContract]);

  useEffect(() => {
    const handleSocketMessage = (event) => {
      try {
        let data;
        if (typeof event.data === "string") {
          data = JSON.parse(event.data);
        } else if (event.detail) {
          data = event.detail;
        } else {
          return;
        }

        if (data.command === "contract_status_update") {
          // Ensure's update is for the current channel
          if (data.channel_id && data.channel_id !== chatChannelId) {
            return;
          }

          // Update the UI with consistent status mapping
          const statusLower = data.status?.toLowerCase();
          setState((prev) => ({
            ...prev,
            contractId: data.contract_id,
            contractStatus: mapBackendStatus(data.status),
            contractFileUrl: data.file_link,
            loading: statusLower === "pending" || statusLower === "generating",
            loadingPdf:
              statusLower === "success" && data.file_link
                ? true
                : prev.loadingPdf,
            error:
              statusLower === "failed"
                ? data.error || "Contract generation failed"
                : null,
          }));

          // Check for completion status variations (case-insensitive)
          const status = data.status?.toLowerCase();
          if (
            status === "success" ||
            status === "completed" ||
            status === "failed"
          ) {
            // Use debounced fetch to prevent race conditions
            debouncedFetchContracts();
          }
        }

        // Handle contract progress updates
        if (data.command === "contract_progress_update") {
          const statusLower = data.status?.toLowerCase();

          if (data.channel_id && data.channel_id !== chatChannelId) {
            return;
          }

          if (statusLower === "failed") {
             setState((prev) => {
               const newRetryCount = prev.retryCount + 1;
               // Auto-retry only once and only if still in entry phase
               if (newRetryCount <= 1 && prev.generationPhase === "entry") {
                 setTimeout(() => {
                   handleGenerateContractRef.current?.();
                 }, 2000);
               }

               const friendlyError = getErrorMessage(data.error, newRetryCount);
               return {
                 ...prev,
                 loading: false,
                 error: friendlyError,
                 retryCount: newRetryCount,
               };
             });
           } else if (statusLower === "completed" || statusLower === "success") {
           setState((prev) => ({
             ...prev,
             loading: false,
             generationProgress: 100,
             generationPhase: "completed",
           }));

           // Use debounced fetch to prevent race conditions
           debouncedFetchContracts();
          } else {
            // Progress update
            setState((prev) => ({
              ...prev,
              generationProgress: data.progress || 0,
              progressMessage: data.message || "Generating contract...",
            }));
          }
        }
      } catch (error) {
        console.error("Error processing socket message:", error);
      }
    };

    const mapBackendStatus = (backendStatus) => {
      const statusMap = {
        pending: "PENDING",
        success: "COMPLETED",
        failed: "FAILED",
        GENERATING: "GENERATING",
        COMPLETED: "COMPLETED",
        FAILED: "FAILED",
        PENDING: "PENDING",
      };
      return statusMap[backendStatus] || backendStatus.toUpperCase();
    };

    const unsubscribe = subscribeToSocketMessages((data) => {
      if (data?.command === "contract_status_update") {
        handleSocketMessage({ detail: data });
      }
      if (data?.command === "contracts") {
        if (data.error) {
          console.error("Error fetching contracts:", data.error);
          setState((prev) => ({
            ...prev,
            error: data.error,
            loading: false,
          }));
          return;
        }

        if (Array.isArray(data?.contracts)) {
          setContracts(data.contracts);

          setState((prev) => ({
            ...prev,
            error: null,
          }));
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [chatChannelId, debouncedFetchContracts, sendCommand, subscribeToSocketMessages]);

  // Description validation
  useEffect(() => {
    const wordCount =
      contractDetails.description?.trim().split(/\s+/).filter(Boolean).length ||
      0;
    const isValid = wordCount >= 10 && wordCount <= 700;
    setState((prev) => ({ ...prev, descriptionValid: isValid }));
  }, [contractDetails.description]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setContractDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (contracts.length > 0 && isOpen) {
      const currentContract = contracts[0];

      updateState({
        contractId: currentContract.id,
        contractFileUrl: currentContract.file_link,
        contractStatus: currentContract.status,
        loadingPdf: !!currentContract.file_link,
        pdfError: null,
      });

      // Only set contract details from history if we don't already have a description
      // This prevents overriding user input or reset state
      setContractDetails((prev) => ({
        title: currentContract.title || "Contract Agreement",
        description: prev.description || currentContract.description || "",
        chat_channel_id: chatChannelId,
      }));
    } else if (contracts.length === 0 && isOpen) {
      // If no contracts exist, ensure we have clean state
      setContractDetails((prev) => ({
        ...prev,
        title: "Contract Agreement",
        description: prev.description || "", // Keep user input if any
        chat_channel_id: chatChannelId,
      }));
    }
  }, [contracts, chatChannelId, updateState, isOpen]);

  if (!mounted) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="5xl"
      classNames={{
        body: "p-0 m-0",
        header: "p-2 m-0",
        base: "p-2 m-0 max-h-[90vh] overflow-y-auto",
      }}
    >
      <ModalContent className="bg-white rounded-xl border border-surface-border">
        <ModalHeader>
          <h2 className="text-xl font-bold">Contract Preview</h2>
        </ModalHeader>
        <ModalBody className="">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="w-full lg:w-1/2 flex flex-col">
              <div className="h-full w-full">
                <div className="border border-surface-border rounded-lg bg-white overflow-y-auto h-[225px] md:h-[400px] lg:h-[600px] max-h-[70vh]">
                  <PreviewContent
                    loading={state.loading}
                    contractStatus={state.contractStatus}
                    contractHistory={contracts}
                    selectedContractIndex={state.selectedContractIndex}
                    generationProgress={state.generationProgress}
                    progressMessage={state.progressMessage}
                    error={state.error}
                    onRetry={handleGenerateContract}
                    retryCount={state.retryCount}
                  />
                </div>
              </div>
              <div className="flex justify-between items-center mt-2">
                <div className="flex gap-2">
                  <button
                    aria-label="Previous contract"
                    className="p-1 hover:bg-surface-hover rounded-full border disabled:opacity-50 transition-all duration-200"
                    onClick={() => {
                      if (
                        state.selectedContractIndex > 0 &&
                        !state.isTransitioning
                      ) {
                        // Set transitioning state
                        updateState({ isTransitioning: true });

                        const newIndex = state.selectedContractIndex - 1;

                        // Update form with selected contract details
                        const selectedContract = contracts[newIndex];
                        if (selectedContract) {
                          setContractDetails({
                            ...contractDetails,
                            title:
                              selectedContract.title || "Contract Agreement",
                            description: selectedContract.description || "",
                          });
                        }

                        // Update index and clear transitioning state after a short delay
                        setTimeout(() => {
                          updateState({
                            selectedContractIndex: newIndex,
                            isTransitioning: false,
                          });
                        }, 100);
                      }
                    }}
                    disabled={
                      state.selectedContractIndex === 0 ||
                      contracts.length === 0 ||
                      state.isTransitioning
                    }
                  >
                    {" "}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-5 h-5 text-mediumgrey"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>

                  <button
                    aria-label="Next contract"
                    onClick={() => {
                      if (
                        state.selectedContractIndex < contracts.length - 1 &&
                        !state.isTransitioning
                      ) {
                        // Set transitioning state
                        updateState({ isTransitioning: true });

                        const newIndex = state.selectedContractIndex + 1;

                        // Update form with selected contract details
                        const selectedContract = contracts[newIndex];
                        if (selectedContract) {
                          setContractDetails({
                            ...contractDetails,
                            title:
                              selectedContract.title || "Contract Agreement",
                            description: selectedContract.description || "",
                          });
                        }

                        // Update index and clear transitioning state after a short delay
                        setTimeout(() => {
                          updateState({
                            selectedContractIndex: newIndex,
                            isTransitioning: false,
                          });
                        }, 100);
                      }
                    }}
                    disabled={
                      state.selectedContractIndex >= contracts.length - 1 ||
                      contracts.length === 0 ||
                      state.isTransitioning
                    }
                    className="p-1 hover:bg-surface-hover rounded-full border disabled:opacity-50 transition-all duration-200"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-5 h-5 text-mediumgrey"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>

                  {contracts.length > 0 && (
                    <span className="text-xs text-mediumgrey self-center ml-2">
                      {state.selectedContractIndex + 1} of {contracts.length}
                    </span>
                  )}
                </div>
                <div className="w-3/12 flex space-x-2">
                  <Button
                    className="px-3 py-1 bg-green-600 text-white rounded-md text-sm hover:bg-green-700"
                    onClick={() => {
                      const fileUrl =
                        contracts[state.selectedContractIndex]?.file_link;
                      if (fileUrl) window.open(fileUrl, "_blank");
                    }}
                  >
                    Download
                  </Button>
                </div>
              </div>
            </div>

            <div className="w-full lg:w-1/2">
              <div className="flex justify-between w-full">
                <h3 className="text-lg font-semibold mb-4">Contract details</h3>

                <Tooltip
                  content={
                    <div className="max-w-xs p-2 bg-white">
                      <h4 className="text-sm font-medium text-primary mb-1">
                        Tip for Best Results
                      </h4>
                      <p className="text-xs text-grey">
                        For the most accurate contract, provide{" "}
                        <strong>full details</strong> in your conversation above
                        including: parties involved, specific terms, dates,
                        amounts, conditions, and any special requirements. The
                        more context you provide, the better your contract will
                        be!
                      </p>
                    </div>
                  }
                  placement="top"
                  className="max-w-xs"
                >
                  <svg
                    className="h-5 w-5 text-secondary-dark cursor-help hover:text-secondary-dark transition-colors"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </Tooltip>
              </div>
              <div className="space-y-4">
                <div className="relative">
                  <textarea
                    placeholder="Enter contract description (10-700 words required)"
                    name="description"
                    value={contractDetails.description}
                    onChange={handleChange}
                    className="w-full min-h-40 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  {/* Enhanced validation UI */}
                  {(() => {
                    const wordCount = contractDetails.description
                      ?.trim()
                      .split(/\s+/)
                      .filter(Boolean).length || 0;
                    const validationState = 
                      wordCount === 0 ? 'empty' :
                      wordCount < 10 ? 'too_short' :
                      wordCount > 700 ? 'too_long' :
                      'valid';

                    return (
                      <div className="flex justify-between items-center mt-2">
                        <span className={`text-sm font-medium ${
                          validationState === 'valid' ? 'text-green-600' :
                          validationState === 'empty' ? 'text-mediumgrey' :
                          'text-danger'
                        }`}>
                          {validationState === 'empty' && 'Enter description (10-700 words)'}
                          {validationState === 'too_short' && `${10 - wordCount} more words needed`}
                          {validationState === 'too_long' && `${wordCount - 700} words over limit`}
                          {/* {validationState === 'valid' && 'Ready to generate'} */}
                        </span>
                        <span className={`text-xs font-medium ${
                          wordCount > 700 ? 'text-danger' : 'text-darkgrey'
                        }`}>
                          {wordCount}/700
                        </span>
                      </div>
                    );
                  })()}
                </div>

                <Button
                  className="w-full bg-primary text-white"
                  onClick={handleGenerateContract}
                  disabled={!state.descriptionValid || state.loading}
                >
                  {state.loading
                    ? "Generating Contract..."
                    : state.contractFileUrl
                      ? "Generate New Contract"
                      : "Generate Contract"}
                </Button>
                {state.error && (
                  <Alert className="mt-2" variant="flat" color="danger">
                    {state.error}
                  </Alert>
                )}
              </div>
            </div>
          </div>
          {contracts.length >= 5 && (
            <p className="text-xs text-danger text-left">
              Only the latest five contracts and histories are kept; anything
              older than five days is automatically deleted.
            </p>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

// PreviewContent component separated from renderPreviewContent function
const PreviewContent = ({
  loading,
  contractStatus,
  contractHistory,
  selectedContractIndex,
  generationProgress,
  progressMessage,
  error,
  onRetry,
  retryCount,
}) => {
  // Helper function to get file URL from contract object
  const getFileUrl = (contract) => {
    return (
      contract?.file_link ||
      contract?.file_url ||
      contract?.fileUrl ||
      contract?.url
    );
  };

  // Note: Iframe management simplified - React re-renders with key change
  // No manual DOM manipulation needed

  if (loading) {
    return (
      <ProgressiveContractLoader
        contractTitle={contractHistory[0]?.title || "Contract Agreement"}
        progress={generationProgress}
        progressMessage={progressMessage}
        error={error}
        onRetry={onRetry}
        retryCount={retryCount}
      />
    );
  }

  if (contractStatus === "pending") {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Spinner size="lg" color="primary" className="mb-4" />
        <p className="text-lg font-semibold mb-2">Generating Contract</p>
        <p className="text-darkgrey">
          Please wait while we generate your contract...
        </p>
      </div>
    );
  }

  if (contractHistory && contractHistory.length > 0) {
    // Get the selected contract from history based on the selected index
    const selectedContract = contractHistory[selectedContractIndex];

    return (
      <div className="flex flex-col h-full">
        {selectedContract && getFileUrl(selectedContract) ? (
          <>
            <div className="flex justify-between items-center mb-0">
              <div className="flex w-full justify-end space-x-2">
                <a
                  href={getFileUrl(selectedContract)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-secondary-dark hover:underline text-xs"
                >
                  Open in new tab
                </a>
                <button
                  onClick={() => {
                    const link = document.createElement("a");
                    link.href = getFileUrl(selectedContract);
                    link.download = `contract_${selectedContract.id || "document"}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="text-secondary-dark hover:underline text-xs"
                >
                  Download
                </button>
              </div>
            </div>
            <div className="overflow-hidden h-full border border-surface-border">
              <iframe
                key={`contract-${selectedContract.id}-${selectedContractIndex}`}
                src={getFileUrl(selectedContract)}
                width="100%"
                height="100%"
                className="border-none"
                title="Contract PDF"
                sandbox="allow-scripts allow-same-origin"
              />
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full">
            <p className="text-darkgrey">
              No contract file available for this version
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-full text-mediumgrey">
      <p>No contract available</p>
      <p className="text-sm mt-2">Generate a contract to view it here</p>
    </div>
  );
};
